// Контакти для «Переказ на картку» у Firebase Realtime Database.
//
// contacts/{chatId}/{contactId}:
//   name      — ім'я з телефонної книги (або отримувача переказу)
//   tel       — перший номер (як у контакті)
//   card      — номер картки, 16 цифр («Зберегти картку» після переказу)
//   recipient — { chatId, cardId, cardType, currency }, якщо картка належить
//               користувачу застосунку
//   photo     — data:image/jpeg, зменшене до 96×96 (або відсутнє)
//   favorite  — true, якщо в «Обрані»
//   createdAt — мс від epoch
//
// Телефонну книгу читаємо лише через Contact Picker API
// (navigator.contacts.select): користувач сам вибирає контакти в системному
// вікні. В iOS він вимкнений за замовчуванням (Settings → Safari → Advanced
// → Feature Flags → Contact Picker API).
import { useEffect, useState } from 'react';
import { ref, onValue, push, set, update, remove } from 'firebase/database';
import { database } from './firebaseConfig';

const cacheKey = (chatId) => `contacts_${chatId}`;

function readCache(chatId) {
  try { return JSON.parse(localStorage.getItem(cacheKey(chatId))) || []; } catch (e) { return []; }
}

export function useContacts(chatId) {
  const [contacts, setContacts] = useState(() => readCache(chatId));

  useEffect(() => {
    if (!chatId) return undefined;
    return onValue(ref(database, `contacts/${chatId}`), (snap) => {
      const val = snap.val() || {};
      const list = Object.keys(val).map((id) => ({ id, ...val[id] }));
      try { localStorage.setItem(cacheKey(chatId), JSON.stringify(list)); } catch (e) { /* ignore */ }
      setContacts(list);
    });
  }, [chatId]);

  return contacts;
}

export const contactPickerSupported = () =>
  typeof navigator !== 'undefined' && 'contacts' in navigator && 'ContactsManager' in window;

// Іконка з пікера (Blob) чи фото отримувача (data URL) будь-якого розміру —
// в БД кладемо маленький JPEG
function blobToThumb(blob, size = 96) {
  return srcToThumb(URL.createObjectURL(blob), size);
}

function srcToThumb(url, size = 96) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const side = Math.min(img.width, img.height);
      canvas.getContext('2d').drawImage(
        img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, size, size,
      );
      URL.revokeObjectURL(url);
      // картинка з іншого домену без CORS «псує» canvas — тоді без фото
      try { resolve(canvas.toDataURL('image/jpeg', 0.8)); } catch (e) { resolve(null); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

// Відкриває системний пікер і дописує вибрані контакти (без дублів за
// номером/ім'ям). Повертає кількість доданих; 0 — нічого не вибрали.
export async function pickAndSaveContacts(chatId, existing = []) {
  const supported = await navigator.contacts.getProperties().catch(() => ['name', 'tel']);
  const props = ['name', 'tel', 'icon'].filter((p) => supported.includes(p));
  const picked = await navigator.contacts.select(props, { multiple: true });

  const seen = new Set(existing.map((c) => c.tel || c.name));
  let added = 0;
  for (const c of picked) {
    const name = (c.name && c.name[0]) || (c.tel && c.tel[0]) || '';
    const tel = (c.tel && c.tel[0]) || '';
    if (!name || seen.has(tel || name)) continue;
    seen.add(tel || name);
    const photo = c.icon && c.icon[0] ? await blobToThumb(c.icon[0]) : null;
    const entry = { name, tel, favorite: false, createdAt: Date.now() };
    if (photo) entry.photo = photo;
    await set(push(ref(database, `contacts/${chatId}`)), entry);
    added += 1;
  }
  return added;
}

// «Зберегти картку» на екрані успіху. Повертає id контакту.
export async function saveCardContact(chatId, { name, cardNumber, photo, recipient }) {
  const entry = { name, card: cardNumber, favorite: false, createdAt: Date.now() };
  if (recipient) {
    const { chatId: rChatId, cardId, cardType, currency } = recipient;
    entry.recipient = { chatId: rChatId, cardId, cardType, currency };
  }
  if (photo) {
    const thumb = await srcToThumb(photo).catch(() => null);
    if (thumb) entry.photo = thumb;
  }
  const r = push(ref(database, `contacts/${chatId}`));
  await set(r, entry);
  return r.key;
}

export function removeContact(chatId, contactId) {
  return remove(ref(database, `contacts/${chatId}/${contactId}`));
}

export function setContactFavorite(chatId, contactId, favorite) {
  return update(ref(database, `contacts/${chatId}/${contactId}`), { favorite });
}
